import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { GROUP_SIZES, BUDGETS, DURATIONS } from "@/data/siteData";
import { getReferral } from "@/lib/referral";

const WHO = ["School", "College", "Corporate", "Friends", "Family", "Other"];

function ChipGroup({ label, options, value, onChange, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[11px] font-black uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                    <button
                        type="button"
                        key={o}
                        data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => onChange(o)}
                        className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                            value === o
                                ? "bg-saffron border-saffron text-white"
                                : "border-white/20 text-white/70 hover:border-gold hover:text-gold"
                        }`}
                    >
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}

export function TripBuilder() {
    const [sel, setSel] = useState({ who: "", size: "", destinationChoice: "", budget: "", duration: "" });
    const [info, setInfo] = useState({ name: "", phone: "", destination: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const setSelKey = (k) => (v) => setSel((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!info.name.trim() || !info.phone.trim()) {
            toast.error("Please add your name and phone number.");
            return;
        }
        if (!/^\d{10,12}$/.test(info.phone)) {
            toast.error("Please enter a valid 10-digit mobile number.");
            return;
        }
        setLoading(true);
        try {
            await submitLead({
                name: info.name,
                phone: info.phone,
                trip_type: sel.who || null,
                group_size: sel.size || null,
                destination: sel.destinationChoice === "Surprise me" ? "Surprise me" : (info.destination || null),
                budget: sel.budget || null,
                duration: sel.duration || null,
                associate_code: getReferral() || null,
                source: "trip-builder",
            });
            setDone(true);
            toast.success("Awesome! Our Trip Expert will get in touch with you shortly.");
        } catch {
            toast.error("Could not submit right now. Please WhatsApp us instead.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="trip-builder" className="py-20 bg-navy-deep text-white" data-testid="trip-builder-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block">banate hain kuch khaas</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">Tell Us What You Want. We'll Build It.</h2>
                <p className="mt-3 text-white/60 text-sm sm:text-base">Every group is different. So why should every trip look the same?</p>

                {done ? (
                    <div className="mt-10 rounded-2xl border border-gold/40 bg-gold/10 p-10 text-center" data-testid="trip-builder-success">
                        <CheckCircle2 className="h-12 w-12 text-gold mx-auto" />
                        <h3 className="mt-4 font-display font-black text-2xl">Awesome!</h3>
                        <p className="mt-2 text-white/70">Our Trip Expert will get in touch with you shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 space-y-7" data-testid="trip-builder-form">
                        <ChipGroup label="Who are you travelling with?" options={WHO} value={sel.who} onChange={setSelKey("who")} testId="tb-who" />
                        <ChipGroup label="Number of travellers" options={GROUP_SIZES} value={sel.size} onChange={setSelKey("size")} testId="tb-size" />
                        <ChipGroup label="Destination" options={["I have a destination", "Surprise me"]} value={sel.destinationChoice} onChange={setSelKey("destinationChoice")} testId="tb-dest" />
                        {sel.destinationChoice === "I have a destination" && (
                            <input
                                data-testid="tb-destination-input"
                                className="w-full rounded-lg border border-white/15 bg-navy-deep/60 px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-saffron"
                                placeholder="Where to? Goa, Manali, Kashmir…"
                                value={info.destination}
                                onChange={(e) => setInfo((i) => ({ ...i, destination: e.target.value }))}
                            />
                        )}
                        <ChipGroup label="Budget per person" options={BUDGETS} value={sel.budget} onChange={setSelKey("budget")} testId="tb-budget" />
                        <ChipGroup label="Trip duration" options={DURATIONS} value={sel.duration} onChange={setSelKey("duration")} testId="tb-duration" />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                            <input data-testid="tb-name-input" className="w-full rounded-lg border border-white/15 bg-navy-deep/60 px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-saffron" placeholder="Your name *" value={info.name} onChange={(e) => setInfo((i) => ({ ...i, name: e.target.value }))} />
                            <input data-testid="tb-phone-input" type="tel" inputMode="numeric" autoComplete="tel" maxLength={12} className="w-full rounded-lg border border-white/15 bg-navy-deep/60 px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-saffron" placeholder="Phone / WhatsApp *" value={info.phone} onChange={(e) => setInfo((i) => ({ ...i, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))} />
                        </div>

                        <button type="submit" disabled={loading} data-testid="tb-submit-button" className="w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-4 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            Build My Trip
                        </button>
                    </form>
                )}
            </div>
        </section>
    );
}
